import React, { useState } from 'react';
import { Box, Button, Paper, Typography, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Papa from 'papaparse';

const FileUpload = ({ onFileUpload }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value ? value.trim() : '',
      complete: (result) => {
        setIsLoading(false);
        if (result.errors.length > 0) {
          setError(`Error parsing CSV file: ${result.errors[0].message} at row ${result.errors[0].row + 1}`);
          console.error(result.errors);
          return;
        }
        
        // Process and validate the data
        const processedData = processData(result.data);
        onFileUpload(processedData);
      },
      error: (error) => {
        setIsLoading(false);
        setError('Error reading the file. Please try again.');
        console.error(error);
      }
    });
  };

  const processData = (data) => {
    // Filter out any empty rows
    const filteredData = data.filter(row => row['Trip ID']);
    
    // Group rows by Trip ID for processing
    const tripGroups = {};
    
    // First pass - group all rows by their Trip ID
    filteredData.forEach(row => {
      const tripId = String(row['Trip ID']);
      if (!tripGroups[tripId]) {
        tripGroups[tripId] = [];
      }
      tripGroups[tripId].push(row);
    });
    
    // Process each trip group
    const trips = [];
    
    Object.entries(tripGroups).forEach(([tripId, rows]) => {
      // Extract the base trip data from the first row of each group
      const firstRow = rows[0];
      
      const trip = {
        id: firstRow['Trip ID'],
        type: firstRow['Type'],
        associatedTripId: firstRow['Associated Trip ID'],
        startTime: firstRow['Start Time'],
        endTime: firstRow['End Time'],
        startLocation: firstRow['Start Location'],
        endLocation: firstRow['End Location'],
        totalKm: firstRow['Total KM'],
        totalFuel: firstRow['Total Fuel (L)'],
        totalEmissions: firstRow['Total Emissions (kg CO2)'],
        emptyKm: firstRow['Empty KM'],
        emptyFuel: firstRow['Empty Fuel (L)'],
        emptyEmissions: firstRow['Empty Emissions (kg CO2)'],
        roundTripKm: firstRow['Round Trip KM'],
        roundTripFuel: firstRow['Round Trip Fuel (L)'],
        roundTripEmissions: firstRow['Round Trip Emissions (kg CO2)'],
        locationsVisited: firstRow['Locations Visited'] ? firstRow['Locations Visited'].split(', ') : [],
        segments: []
      };
      
      // Process all segments from the rows
      rows.forEach((row, index) => {
        // Add segment if it has segment data
        if (row['Segment From'] && row['Segment To']) {
          trip.segments.push({
            from: row['Segment From'],
            to: row['Segment To'],
            km: row['Segment KM'],
            fuel: row['Segment Fuel (L)'],
            emissions: row['Segment Emissions (kg CO2)']
          });
        }
      });
      
      // For backhaul trips with no segments, create a single segment from start to end
      if (trip.type.toLowerCase().includes('backhaul') && trip.segments.length === 0) {
        trip.segments.push({
          from: trip.startLocation,
          to: trip.endLocation,
          km: trip.totalKm,
          fuel: trip.totalFuel,
          emissions: trip.totalEmissions
        });
      }
      
      trips.push(trip);
    });
    
    return trips;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      const fileInput = document.getElementById('csv-file-input');
      fileInput.files = event.dataTransfer.files;
      handleFileChange({ target: { files: [file] } });
    } else {
      setError('Please upload a CSV file.');
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        borderRadius: 2,
        backgroundColor: 'background.paper'
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        Upload Trip Data CSV
      </Typography>
      
      <Box 
        sx={{ 
          border: '2px dashed #3f51b5', 
          borderRadius: 2, 
          p: 4, 
          width: '100%', 
          maxWidth: 500,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(63, 81, 181, 0.04)'
          }
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById('csv-file-input').click()}
      >
        <input
          type="file"
          accept=".csv"
          id="csv-file-input"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          Drag and drop a CSV file here or click to browse
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          Only CSV files are supported
        </Typography>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Button 
        variant="contained" 
        component="label" 
        startIcon={<CloudUploadIcon />}
        disabled={isLoading}
        sx={{ mt: 2 }}
      >
        Select CSV File
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
        />
      </Button>
    </Paper>
  );
};

export default FileUpload;